import { useTranslation } from 'react-i18next'
import { localizedName } from '../utils/localizedName'
import { useEffect, useState } from 'react'
import {
  Box, Text, Button, Table, Badge, Group, Modal, Stack,
  Textarea, Select, Tabs, ActionIcon, Tooltip, Checkbox,
  Center, Loader, Alert, SimpleGrid, Image, Divider, TextInput, Paper, Card, Menu, Drawer, Indicator,
} from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { useBreakpoint } from '../hooks/useBreakpoint'
import SmartDateInput from '../components/SmartDateInput'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import {
  IconPlus, IconCheck, IconX, IconAlertCircle, IconPhoto, IconPrinter, IconSearch, IconEdit, IconHistory, IconCalendarTime, IconDotsVertical, IconFilter,
} from '@tabler/icons-react'
import {
  getAssets, requestBorrow, approveBorrow, rejectBorrow,
  returnAsset, partialReturnBorrow, getPendingBorrows, getMyBorrows, getAllBorrows,
  getRecipients, getCategories, scanLookupAsset, updateBorrow, getModels, getBorrowHistory, downloadBorrowPdf,
  requestBorrowExtend, approveBorrowExtend, rejectBorrowExtend, getCompanyProfile,
} from '../api/client'
import AssetQuickView from '../components/assets/AssetQuickView'
import { useAuth } from '../hooks/useAuth'
import HistoryTimelineModal from '../components/HistoryTimelineModal'

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_COLOR: Record<string, string> = {
  PENDING: 'yellow', APPROVED: 'teal', REJECTED: 'red',
  RETURNED: 'gray', PARTIALLY_RETURNED: 'orange', OVERDUE: 'red',
}

const EMPTY_FORM = {
  assetIds: [] as number[],
  recipientName: '', recipientPosition: '', recipientEmail: '',
  borrowerEmail: '', reason: '', dueDate: '',
  notificationEmail: '',
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function BorrowPage() {
  const { t, i18n } = useTranslation()
  const { roles, username } = useAuth()
  const { isMobile, isTablet, isSmall } = useBreakpoint()
  const isApprover = roles.includes('ADMIN') || roles.includes('ASSET_MANAGER')
  const [qvAsset, setQvAsset] = useState<any>(null)
  const [qvOpen, setQvOpen] = useState(false)
  const openQv = (code: string) => scanLookupAsset(code).then(r => { if (r.data?.found) { setQvAsset(r.data); setQvOpen(true) } })
  const [historyOpened, { open: openHistory, close: closeHistory }] = useDisclosure(false)
  const [historyTarget, setHistoryTarget] = useState<any>(null)
  const [historyData, setHistoryData] = useState<any[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const handleOpenHistory = async (b: any) => {
    setHistoryTarget(b)
    setHistoryData([])
    openHistory()
    setLoadingHistory(true)
    try {
      const res = await getBorrowHistory(b.id)
      setHistoryData(res.data)
    } catch (_) {
      notifications.show({ color: 'red', message: t('borrow.historyLoadFailed', 'Failed to load history') })
    } finally { setLoadingHistory(false) }
  }

  const [pending, setPending]         = useState<any[]>([])
  const [myBorrows, setMyBorrows]     = useState<any[]>([])
  const [allBorrows, setAllBorrows]   = useState<any[]>([])
  const [search, setSearch]           = useState('')
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  // ── Filter bottom sheet (มือถือ) — เหมือน pattern เดียวกับ AssetsPage.tsx แม้จะมีแค่ตัวกรอง
  // เดียว (status) ก็ตาม ทำให้เหมือนกันทั้งแอปตามที่ขอ ──
  const [filterSheetOpen, setFilterSheetOpen] = useState(false)
  const [draftStatus, setDraftStatus] = useState<string | null>(null)
  const openFilterSheet = () => { setDraftStatus(statusFilter); setFilterSheetOpen(true) }
  const applyFilterSheet = () => { setStatusFilter(draftStatus); setFilterSheetOpen(false) }
  const clearFilterSheet = () => setDraftStatus(null)

  /**
   * เทียบว่า dueDate ผ่านไปแล้วจริงไหม — ใช้ตรรกะเดียวกับฝั่ง backend (markOverdueBorrows ใน
   * notifications.service.ts): เทียบแค่ "วันที่" ไม่เทียบเวลา ตัดเวลาทั้งสองฝั่งเป็นเที่ยงคืนก่อนเทียบเสมอ
   *
   * บั๊กเดิม: ใช้ `new Date(b.dueDate) < new Date()` ตรงๆ — dueDate ที่เป็น string วันที่ล้วน
   * ("2026-08-10") ถูก parse เป็นเที่ยงคืนของวันนั้น พอเทียบกับเวลาปัจจุบัน (ซึ่งมีเวลาแปะมาด้วย)
   * ผลคือขึ้น "เกินกำหนด" ตั้งแต่ 00:00 ของวันครบกำหนดเอง ทั้งที่ยังไม่เกินจริง (ต้องเกินกำหนดตั้งแต่
   * วันถัดไป) — backend คำนวณถูกอยู่แล้ว (ตัดเวลาออกก่อนเทียบ) จุดนี้เป็นแค่โค้ดสำรองฝั่งหน้าเว็บที่ยัง
   * ไม่ตรงกับ backend
   */
  const isPastDueDate = (dueDate: string | Date | null | undefined): boolean => {
    if (!dueDate) return false
    const due = new Date(dueDate)
    due.setHours(0, 0, 0, 0)
    const startOfToday = new Date()
    startOfToday.setHours(0, 0, 0, 0)
    return due < startOfToday
  }

  /**
   * ตัวกรองสถานะของการ์ดสรุป — ปกติเทียบ status ตรงๆ (เช่น 'RETURNED') แต่ 'ACTIVE_ALL'/'OVERDUE_ALL'
   * เป็นค่าพิเศษที่ต้องครอบคลุมมากกว่า 1 สถานะ เพราะตอนนี้ "เกินกำหนด" อาจเป็นได้ทั้ง
   * สถานะ OVERDUE จริงที่ cron ตั้งให้ (ดู markOverdueBorrows ฝั่ง backend) หรือ APPROVED/
   * PARTIALLY_RETURNED ที่ dueDate ผ่านไปแล้วแต่ cron ยังไม่ทันรัน — ถ้าเทียบตรงๆ แค่ 'APPROVED'
   * เหมือนเดิม รายการที่ถูก cron เปลี่ยนเป็น OVERDUE ไปแล้วจะหายไปจากการ์ด "เกินกำหนด"/"ใช้งานอยู่" ทันที
   */
  const matchesStatusFilter = (b: any, filter: string | null) => {
    if (!filter) return true
    if (filter === 'ACTIVE_ALL') return b.status === 'APPROVED' || b.status === 'PARTIALLY_RETURNED' || b.status === 'OVERDUE'
    if (filter === 'OVERDUE_ALL') return b.status === 'OVERDUE' || ((b.status === 'APPROVED' || b.status === 'PARTIALLY_RETURNED') && isPastDueDate(b.dueDate))
    return b.status === filter
  }
  const [activeTab, setActiveTab]       = useState<string>(isApprover ? 'all' : 'my')
  // ── การ์ดสรุปยอด: เก็บ "key ของการ์ดที่เพิ่งกด" แยกจาก activeTab ──
  // ของเดิมเช็คไฮไลต์จาก `activeTab === card.tab` แต่การ์ด Total/Active/Overdue/Returned/Rejected
  // ใช้ tab ค่าเดียวกันหมด (isApprover ? 'all' : 'my') พอกดการ์ดไหนก็ตาม จะสว่างพร้อมกันทั้ง 5 การ์ด
  // เพราะเงื่อนไขเป็นจริงพร้อมกันหมด — แก้ด้วยการแยก state ไว้บอกว่า "การ์ดไหนที่เพิ่งกดจริงๆ"
  const [selectedCard, setSelectedCard] = useState<string>('total')
  const [assets, setAssets]           = useState<any[]>([])
  const [recipients, setRecipients]   = useState<any[]>([])
  const [categories, setCategories]   = useState<any[]>([])
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null)
  const [assetSearch, setAssetSearch] = useState('')
  const [modalKey, setModalKey] = useState(0)

  const openRequestModal = () => {
    setModalKey(k => k + 1)
    setEditingBorrowId(null)
    setForm({
      assetIds: [],
      recipientName: '', recipientPosition: '', recipientEmail: '',
      borrowerEmail: '', reason: '', dueDate: '',
      notificationEmail: '',
    })
    setAssetSearch('')
    setCategoryFilter(null)
    open()
  }

  const openEditModal = (b: any) => {
    setModalKey(k => k + 1)
    setEditingBorrowId(b.id)
    setForm({
      assetIds: (b.assets ?? []).map((a: any) => a.id),
      recipientName: b.recipientName ?? '',
      recipientPosition: b.recipientPosition ?? '',
      recipientEmail: b.recipientEmail ?? '',
      borrowerEmail: b.borrowerEmail ?? '',
      reason: b.reason ?? '',
      dueDate: b.dueDate ? b.dueDate.slice(0, 10) : '',
      notificationEmail: (b as any).notificationEmail ?? '',
    })
    setAssetSearch('')
    setCategoryFilter(null)
    open()
  }
  const [loading, setLoading]         = useState(true)
  const [opened, { open, close }]     = useDisclosure(false)
  const [saving, setSaving]           = useState(false)
  const [editingBorrowId, setEditingBorrowId] = useState<number | null>(null)
  const [approvingBorrow, setApprovingBorrow] = useState<any>(null)
  const [approveDue, setApproveDue]   = useState('')
  const [approveModalOpen, { open: openApproveModal, close: closeApproveModal }] = useDisclosure(false)
  const [form, setForm]               = useState({ ...EMPTY_FORM })

  const load = async () => {
    setLoading(true)
    try {
      const [a, my, rec, cats, mods] = await Promise.all([
        getAssets(), getMyBorrows(), getRecipients(), getCategories(), getModels(),
      ])
      // Build model imageUrl map แบบเดียวกับ HandoverPage
      const modelImageMap: Record<string, string> = {}
      ;(mods.data ?? []).forEach((m: any) => { if (m.code && m.imageUrl) modelImageMap[m.code] = m.imageUrl })
      const assetsWithImage = (a.data ?? []).map((asset: any) => ({
        ...asset,
        imageUrl: asset.imageUrl || (asset.model ? modelImageMap[asset.model] : undefined),
      }))
      setAssets(assetsWithImage)
      setMyBorrows(my.data)
      setRecipients(rec.data.filter((r: any) => r.isActive !== false))
      setCategories(cats.data.filter((c: any) => c.isActive !== false))
      if (isApprover) {
        const [p, all] = await Promise.all([getPendingBorrows(), getAllBorrows()])
        setPending(p.data)
        setAllBorrows(all.data)
      }
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  // ── Borrow Policy — โหลดครั้งเดียวไว้ตรวจฝั่ง client ก่อนส่งคำขอเลื่อน (UX เร็วขึ้น ไม่ต้องรอ
  // backend ตีกลับ) backend ยัง validate ซ้ำอีกชั้นเสมออยู่ดี (ห้ามเชื่อ client ฝ่ายเดียว) ──
  const [borrowPolicy, setBorrowPolicy] = useState<{ maxExtensionDays: number; maxExtensionCount: number | null } | null>(null)
  useEffect(() => {
    getCompanyProfile().then(({ data }) => {
      setBorrowPolicy({ maxExtensionDays: data.maxExtensionDays ?? 7, maxExtensionCount: data.maxExtensionCount ?? null })
    }).catch(() => {})
  }, [])

  const reservedBorrowIds = new Set(
    allBorrows.filter((b: any) => b.status === 'PENDING' || b.status === 'APPROVED' || b.status === 'PARTIALLY_RETURNED' || b.status === 'OVERDUE')
      .flatMap((b: any) => (b.assets ?? []).map((a: any) => a.id))
  )

  const availableAssets = assets.filter((a: any) => {
    if (a.status !== 'AVAILABLE') return false
    if (reservedBorrowIds.has(a.id) && !form.assetIds.includes(a.id)) return false
    if (categoryFilter && a.category !== categoryFilter) return false
    const q = assetSearch.toLowerCase().trim()
    if (!q) return true
    // ค้นหาด้วย Name หรือ Serial Number เท่านั้น (ไม่รวม Asset Code ตามที่ขอ)
    return (
      a.name?.toLowerCase().includes(q) ||
      a.serialNumber?.toLowerCase().includes(q)
    )
  })

  const toggleAsset = (id: number) =>
    setForm(p => ({
      ...p,
      assetIds: p.assetIds.includes(id)
        ? p.assetIds.filter(x => x !== id)
        : [...p.assetIds, id],
    }))

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const handleRequest = async () => {
    if (form.assetIds.length === 0) {
      notifications.show({ color: 'red', message: t('borrow.selectAtLeastOne', 'Please select at least 1 asset') })
      return
    }
    setSaving(true)
    try {
      const payload = {
        assetIds:          form.assetIds,
        recipientName:     form.recipientName  || undefined,
        recipientPosition: form.recipientPosition || undefined,
        recipientEmail:    form.recipientEmail || undefined,
        borrowerEmail:     form.borrowerEmail  || undefined,
        reason:            form.reason         || undefined,
        dueDate:           form.dueDate        || undefined,
        notificationEmail: (form as any).notificationEmail || undefined,
      }
      if (editingBorrowId) {
        await updateBorrow(editingBorrowId, payload)
        notifications.show({ color: 'teal', message: t('borrow.updatedMsg', 'Borrow request updated') })
      } else {
        await requestBorrow(payload)
        notifications.show({ color: 'lime', message: t('borrow.requestSentMsg', 'Request sent — awaiting approval') })
      }
      close(); load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('common.error', 'An error occurred') })
    } finally { setSaving(false) }
  }

  const handleApprove = (borrow: any) => {
    setApprovingBorrow(borrow)
    // Pre-fill due date จาก request ถ้ามี ไม่งั้นว่างให้ Admin กรอก
    setApproveDue(borrow.dueDate ? borrow.dueDate.slice(0, 10) : '')
    openApproveModal()
  }

  const confirmApprove = async () => {
    if (!approvingBorrow) return
    try {
      await approveBorrow(approvingBorrow.id, { dueDate: approveDue || undefined })
      notifications.show({ color: 'lime', message: t('common.approvedMsg', 'Approved') })
      closeApproveModal()
      load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
    }
  }

  const handleReject = (borrow: any) => {
    let reason = ''
    modals.openConfirmModal({
      title: t('borrow.rejectRequestTitle', 'Reject Borrow Request'),
      children: (
        <Stack gap="sm">
          <Text size="sm">{t('borrow.rejectRequestConfirm', 'Reject the borrow request from')} <b>{borrow.borrowerName}</b></Text>
          <Textarea label={t("borrow.rejectReason", "Reason")} onChange={(e) => { reason = e.target.value }} />
        </Stack>
      ),
      labels: { confirm: t('borrow.reject', 'Reject'), cancel: t('common.cancel', 'Cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        await rejectBorrow(borrow.id, reason)
        notifications.show({ color: 'lime', message: t('borrow.rejectedMsg', 'Request rejected') })
        load()
      },
    })
  }

  const handleReturn = (borrow: any) => {
    modals.openConfirmModal({
      title: t('borrow.returnAssetTitle', 'Return Asset'),
      children: (
        <Text size="sm">{t('borrow.confirmReturnAll', 'Confirm returning')} <b>{(borrow.assets ?? []).length} {t('common.items', 'item(s)')}</b> — {t('borrow.willBecomeAvailable', 'status will change to Available')}</Text>
      ),
      labels: { confirm: t('borrow.confirmReturn', 'Confirm Return'), cancel: t('common.cancel', 'Cancel') },
      confirmProps: { color: 'lime', style: { background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)' } },
      onConfirm: async () => {
        await returnAsset(borrow.id)
        notifications.show({ color: 'lime', message: t('borrow.returnedMsg', 'Asset returned successfully') })
        load()
      },
    })
  }

  const handlePartialReturn = (borrow: any) => {
    const remainingAssets: any[] = borrow.assets ?? []
    // ถ้าเหลือแค่ 1 ชิ้น ไม่สามารถคืนบางส่วนได้
    if (remainingAssets.length <= 1) {
      notifications.show({ color: 'orange', message: t('borrow.oneItemLeftUseReturnAll', 'Only 1 item left — please use "Return All" instead') })
      return
    }

    // State สำหรับ modal — ใช้ object ชั่วคราว
    const selected = new Set<number>()
    let note = ''

    modals.openConfirmModal({
      title: `${t('borrow.partialReturnTitle', 'Partial Return')} — BRW-${String(borrow.id).padStart(4, '0')}`,
      size: 'md',
      children: (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">{t('borrow.selectAssetsToReturn', 'Select the assets to return (at least 1 must remain)')}</Text>
          <Stack gap={6}>
            {remainingAssets.map((a: any) => (
              <Box key={a.id} style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <Checkbox
                  label={
                    <Box>
                      <Text size="sm" fw={600}>{a.name}</Text>
                      <Text size="xs" c="dimmed">{a.assetCode} · {a.serialNumber}</Text>
                    </Box>
                  }
                  onChange={(e) => {
                    if (e.currentTarget.checked) selected.add(a.id)
                    else selected.delete(a.id)
                  }}
                />
              </Box>
            ))}
          </Stack>
          <Textarea
            label={t('borrow.returnNoteLabel')}
            placeholder={t('borrow.returnNotePlaceholder')}
            onChange={(e) => { note = e.target.value }}
          />
        </Stack>
      ),
      labels: { confirm: t('borrow.confirmReturn', 'Confirm Return'), cancel: t('common.cancel', 'Cancel') },
      confirmProps: { color: 'blue' },
      onConfirm: async () => {
        const ids = Array.from(selected)
        if (ids.length === 0) {
          notifications.show({ color: 'red', message: t('borrow.selectAtLeastOne', 'Please select at least 1 asset') })
          return
        }
        if (ids.length >= remainingAssets.length) {
          notifications.show({ color: 'orange', message: t('borrow.allSelectedUseReturnAll', 'All items selected — please use "Return All" instead') })
          return
        }
        try {
          await partialReturnBorrow(borrow.id, ids, note || undefined)
          notifications.show({ color: 'teal', message: t('borrow.partialReturnedMsg', 'Returned {{count}} item(s)', { count: ids.length }) })
          load()
        } catch (e: any) {
          notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
        }
      },
    })
  }

  // ── Extend (ขอเลื่อนวันคืน) ──────────────────────────────────────────────────
  const handleRequestExtend = (borrow: any) => {
    let newDueDate = ''
    let reason = ''
    const maxDays = borrowPolicy?.maxExtensionDays ?? 7
    const baseDate = borrow.dueDate ? new Date(borrow.dueDate) : new Date()
    const maxAllowed = new Date(baseDate)
    maxAllowed.setDate(maxAllowed.getDate() + maxDays)
    const maxAllowedStr = maxAllowed.toISOString().slice(0, 10)

    modals.openConfirmModal({
      title: `${t('borrow.extendModalTitle', 'Request Extension')} — BRW-${String(borrow.id).padStart(4, '0')}`,
      children: (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">{t('borrow.currentDueDate', 'Current due date')}: {borrow.dueDate ? borrow.dueDate.slice(0, 10) : '—'}</Text>
          <Text size="xs" c="dimmed">{t('borrow.extendMaxDaysHint', 'Up to {{days}} days per request (no later than {{date}})', { days: maxDays, date: maxAllowedStr })}</Text>
          <SmartDateInput
            label={t('borrow.newDueDate', 'New due date')}
            placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
            max={maxAllowedStr}
            onChange={(v) => { newDueDate = v }}
          />
          <Textarea
            label={t('borrow.extendReason', 'Reason')}
            placeholder={t('borrow.extendReasonPlaceholder', 'Please explain why you need to extend the due date')}
            onChange={(e) => { reason = e.target.value }}
            required
          />
        </Stack>
      ),
      labels: { confirm: t('borrow.sendRequest', 'Send Request'), cancel: t('common.cancel', 'Cancel') },
      confirmProps: { color: 'blue' },
      onConfirm: async () => {
        if (!newDueDate) {
          notifications.show({ color: 'red', message: t('borrow.selectNewDueDate', 'Please select a new due date') })
          return
        }
        if (!reason.trim()) {
          notifications.show({ color: 'red', message: t('borrow.extendReasonRequired', 'Please provide a reason for the extension') })
          return
        }
        if (newDueDate > maxAllowedStr) {
          notifications.show({ color: 'red', message: t('borrow.extendMaxDaysError', 'Up to {{days}} days per request (no later than {{date}})', { days: maxDays, date: maxAllowedStr }) })
          return
        }
        try {
          await requestBorrowExtend(borrow.id, newDueDate, reason.trim())
          notifications.show({ color: 'blue', message: t('borrow.extendRequestSent', 'Extension request sent — awaiting approval') })
          load()
        } catch (e: any) {
          notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
        }
      },
    })
  }

  const handleApproveExtend = (borrow: any) => {
    modals.openConfirmModal({
      title: t('borrow.approveExtendTitle', 'Approve Extension'),
      children: (
        <Text size="sm">
          {t('borrow.approveExtendConfirm', 'Approve extending the due date from')} <b>{borrow.dueDate ? borrow.dueDate.slice(0, 10) : '—'}</b> {t('common.to', 'to')}{' '}
          <b>{borrow.pendingExtendDueDate ? String(borrow.pendingExtendDueDate).slice(0, 10) : '—'}</b>?
          {borrow.pendingExtendReason && <><br /><Text size="xs" c="dimmed" mt={4}>{t('borrow.extendReason', 'Reason')}: {borrow.pendingExtendReason}</Text></>}
        </Text>
      ),
      labels: { confirm: t('common.approve', 'Approve'), cancel: t('common.cancel', 'Cancel') },
      confirmProps: { color: 'teal' },
      onConfirm: async () => {
        try {
          await approveBorrowExtend(borrow.id)
          notifications.show({ color: 'teal', message: t('borrow.extendApprovedMsg', 'Extension approved') })
          load()
        } catch (e: any) {
          notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
        }
      },
    })
  }

  const handleRejectExtend = (borrow: any) => {
    let reason = ''
    modals.openConfirmModal({
      title: t('borrow.rejectExtendTitle', 'Reject Extension Request'),
      children: (
        <Stack gap="sm">
          <Text size="sm">{t('borrow.rejectExtendConfirm', 'Reject the request to extend the due date to')} <b>{borrow.pendingExtendDueDate ? String(borrow.pendingExtendDueDate).slice(0, 10) : '—'}</b>?</Text>
          <Textarea label={t('borrow.reasonOptional', 'Reason (optional)')} onChange={(e) => { reason = e.target.value }} />
        </Stack>
      ),
      labels: { confirm: t('borrow.reject', 'Reject'), cancel: t('common.cancel', 'Cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await rejectBorrowExtend(borrow.id, reason || undefined)
          notifications.show({ color: 'gray', message: t('borrow.extendRejectedMsg', 'Extension request rejected') })
          load()
        } catch (e: any) {
          notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
        }
      },
    })
  }

  // ── Table / Card ──────────────────────────────────────────────────────────────

  const BorrowTable = ({ data, showActions = false, showApprove = false }: any) => isMobile ? (
    // ── มือถือ: ยกระดับเป็นการ์ดตาม pattern เดียวกับ Configuration/License/Assets ที่ทำไปแล้ว —
    // ของเดิมเป็นแค่ list คั่นด้วยเส้นใต้ ไม่มีกรอบการ์ด และปุ่มจัดการเป็นไอคอนเล็ก 14px ไม่มี label
    // เลยสักตัว (ไม่มีแม้แต่ tooltip) ต้องเดาเอาว่าปุ่มไหนทำอะไร — รวมเป็นเมนู ⋮ ที่มีข้อความกำกับ
    // ทุกปุ่มแทน (จำนวน action ของ Borrow ไม่เยอะเท่า Asset เลยใช้ dropdown menu พอ ไม่ต้องเป็นกริด)
    <Stack gap="xs" p="xs">
      {data.length === 0 ? (
        <Center p="xl"><Text c="dimmed" size="sm">{t("common.noRecords","No records")}</Text></Center>
      ) : data.map((b: any) => {
        const canReturn = showActions && (b.status === 'APPROVED' || b.status === 'PARTIALLY_RETURNED' || b.status === 'OVERDUE')
        const canApprove = showApprove && b.status === 'PENDING'
        const canApproveExtend = isApprover && b.pendingExtendDueDate
        return (
          <Card key={b.id} withBorder radius="lg" p="md">
            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
              <Box style={{ minWidth: 0, flex: 1 }}>
                <Text size="sm" fw={600} style={{ color: '#0f172a' }}>BRW-{String(b.id).padStart(4, '0')}</Text>
                <Text size="xs" c="dimmed">{b.borrowerName}</Text>
              </Box>
              <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                <Badge size="sm" color={STATUS_COLOR[b.status] ?? 'gray'} variant="light">{b.status}</Badge>
                <Menu shadow="md" width={220} position="bottom-end">
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray" size="lg"><IconDotsVertical size={18} /></ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {canApprove && (
                      <>
                        <Menu.Item leftSection={<IconCheck size={15} color="var(--mantine-color-teal-6)" />} onClick={() => handleApprove(b)}>
                          {t('common.approve', 'Approve')}
                        </Menu.Item>
                        <Menu.Item leftSection={<IconX size={15} color="var(--mantine-color-red-6)" />} onClick={() => handleReject(b)}>
                          {t('borrow.reject', 'Reject')}
                        </Menu.Item>
                      </>
                    )}
                    {canReturn && (
                      <>
                        <Menu.Item leftSection={<IconCheck size={15} color="var(--mantine-color-teal-6)" />} onClick={() => handleReturn(b)}>
                          {t('borrow.returnAll', 'Return All')}
                        </Menu.Item>
                        {(b.assets ?? []).length > 1 && (
                          <Menu.Item leftSection={<IconCheck size={15} color="var(--mantine-color-blue-6)" />} onClick={() => handlePartialReturn(b)}>
                            {t('borrow.partialReturnTooltip', 'Partial return ({{count}} items)', { count: (b.assets ?? []).length })}
                          </Menu.Item>
                        )}
                        {!b.pendingExtendDueDate && (
                          <Menu.Item leftSection={<IconCalendarTime size={15} />} onClick={() => handleRequestExtend(b)}>
                            {t('borrow.requestExtend', 'Request Extension')}
                          </Menu.Item>
                        )}
                      </>
                    )}
                    {canApproveExtend && (
                      <>
                        <Menu.Item leftSection={<IconCheck size={15} color="var(--mantine-color-teal-6)" />} onClick={() => handleApproveExtend(b)}>
                          {t('borrow.approveExtend', 'Approve Extension')}
                        </Menu.Item>
                        <Menu.Item leftSection={<IconX size={15} color="var(--mantine-color-red-6)" />} onClick={() => handleRejectExtend(b)}>
                          {t('borrow.rejectExtend', 'Reject Extension')}
                        </Menu.Item>
                      </>
                    )}
                    <Menu.Item leftSection={<IconPrinter size={15} />} onClick={async () => {
                      try {
                        const res = await downloadBorrowPdf(b.id, i18n.language === 'th' ? 'th' : 'en')
                        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
                        window.open(url, '_blank')
                        setTimeout(() => URL.revokeObjectURL(url), 30_000)
                      } catch { notifications.show({ color: 'red', message: t('borrow.pdfGenerationFailed', 'Failed to generate PDF') }) }
                    }}>
                      {t('common.print', 'Print')}
                    </Menu.Item>
                    <Menu.Item leftSection={<IconHistory size={15} />} onClick={() => handleOpenHistory(b)}>
                      {t('license.viewHistory', 'History')}
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Group>

            <Divider my={8} />

            <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
              <Box>
                {b.recipientName && <Text size="xs" c="dimmed">→ {b.recipientName} {b.recipientPosition ? `(${b.recipientPosition})` : ''}</Text>}
                <Text size="xs" c="dimmed">{t('borrow.assetCount', '{{count}} asset(s)', { count: (b.assets ?? []).length })}</Text>
              </Box>
              {b.dueDate && <Text size="xs" c="dimmed">{t('borrow.dueDateLabel', 'Due')}: {b.dueDate.slice(0, 10)}</Text>}
            </Group>
            {b.pendingExtendDueDate && (
              <Badge color="blue" variant="light" size="xs" mt={8}>{t('borrow.pendingExtendBadge', 'Pending extension')} → {String(b.pendingExtendDueDate).slice(0, 10)}</Badge>
            )}
          </Card>
        )
      })}
    </Stack>
  ) : (
    <Table highlightOnHover>
      <Table.Thead style={{ background: '#f8f7f4' }}>
        <Table.Tr>
          <Table.Th>#</Table.Th>
          <Table.Th>{t("common.assets","Assets")}</Table.Th>
          <Table.Th>{t("borrow.borrower","Borrower")}</Table.Th>
          <Table.Th>{t("common.recipient","Recipient")}</Table.Th>
          <Table.Th>{t("borrow.reason","Reason")}</Table.Th>
          <Table.Th>{t("borrow.dueDate","Due Date")}</Table.Th>
          <Table.Th>{t("common.status","Status")}</Table.Th>
          <Table.Th></Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {data.length === 0 ? (
          <Table.Tr>
            <Table.Td colSpan={8}><Center p="xl"><Text c="dimmed">{t("common.noRecords","No records")}</Text></Center></Table.Td>
          </Table.Tr>
        ) : data.map((b: any) => (
          <Table.Tr key={b.id}>
            <Table.Td><Text fw={700} size="sm">BRW-{String(b.id).padStart(4, '0')}</Text></Table.Td>
            <Table.Td>
              {(b.assets ?? []).slice(0, 2).map((a: any) => (
                <Text key={a.id} size="xs" style={{ cursor: 'pointer', color: '#0d9488', textDecoration: 'underline dotted' }}
                  onClick={() => openQv(a.assetCode)}>
                  {a.assetCode} — {a.name}
                </Text>
              ))}
              {(b.assets ?? []).length > 2 && <Text size="xs" c="dimmed">+{b.assets.length - 2} more</Text>}
            </Table.Td>
            <Table.Td><Text size="sm">{b.borrowerName}</Text></Table.Td>
            <Table.Td>
              {b.recipientName ? (
                <Box>
                  <Text size="sm" fw={600}>{b.recipientName}</Text>
                  {b.recipientPosition && <Text size="xs" c="dimmed">{b.recipientPosition}</Text>}
                </Box>
              ) : <Text size="xs" c="dimmed">—</Text>}
            </Table.Td>
            <Table.Td><Text size="sm" c="dimmed" lineClamp={2}>{b.reason ?? '-'}</Text></Table.Td>
            <Table.Td>
              <Text size="xs" c={(b.status === 'OVERDUE' || (isPastDueDate(b.dueDate) && (b.status === 'APPROVED' || b.status === 'PARTIALLY_RETURNED'))) ? 'red' : 'dimmed'}>
                {b.dueDate ? b.dueDate.slice(0, 10) : '-'}
                {(b.status === 'OVERDUE' || (isPastDueDate(b.dueDate) && (b.status === 'APPROVED' || b.status === 'PARTIALLY_RETURNED'))) && (
                  <Text span size="10px" fw={700} ml={4}>{t("common.overdue","OVERDUE")}</Text>
                )}
              </Text>
              {b.pendingExtendDueDate && (
                <Badge color="blue" variant="light" size="xs" mt={2}>{t('borrow.pendingExtendBadge', 'Pending extension')} → {String(b.pendingExtendDueDate).slice(0, 10)}</Badge>
              )}
            </Table.Td>
            <Table.Td>
              <Badge color={STATUS_COLOR[b.status] ?? 'gray'} variant="light">{b.status}</Badge>
            </Table.Td>
            <Table.Td>
              <Group gap={4}>
                {b.status === 'PENDING' && (
                  <Tooltip label={t("common.edit", "Edit Request")}>
                    <ActionIcon color="blue" variant="subtle" onClick={() => openEditModal(b)}><IconEdit size={16} /></ActionIcon>
                  </Tooltip>
                )}
                {showApprove && b.status === 'PENDING' && (
                  <>
                    <Tooltip label={t("common.approve", "Approve")}>
                      <ActionIcon color="teal" variant="subtle" onClick={() => handleApprove(b)}><IconCheck size={16} /></ActionIcon>
                    </Tooltip>
                    <Tooltip label={t("borrow.reject", "Reject")}>
                      <ActionIcon color="red" variant="subtle" onClick={() => handleReject(b)}><IconX size={16} /></ActionIcon>
                    </Tooltip>
                  </>
                )}
                {showActions && (b.status === 'APPROVED' || b.status === 'PARTIALLY_RETURNED' || b.status === 'OVERDUE') && (
                  <>
                    <Tooltip label={t("borrow.returnAll", "Return All")}>
                      <ActionIcon color="teal" variant="light" onClick={() => handleReturn(b)}><IconCheck size={16} /></ActionIcon>
                    </Tooltip>
                    {(b.assets ?? []).length > 1 && (
                      <Tooltip label={t('borrow.partialReturnTooltip', 'Partial return ({{count}} items)', { count: (b.assets ?? []).length })}>
                        <ActionIcon color="blue" variant="subtle" onClick={() => handlePartialReturn(b)}><IconCheck size={14} /></ActionIcon>
                      </Tooltip>
                    )}
                    {!b.pendingExtendDueDate && (
                      <Tooltip label={t('borrow.requestExtend', 'Request Extension')}>
                        <ActionIcon color="blue" variant="subtle" onClick={() => handleRequestExtend(b)}><IconCalendarTime size={16} /></ActionIcon>
                      </Tooltip>
                    )}
                  </>
                )}
                {isApprover && b.pendingExtendDueDate && (
                  <>
                    <Tooltip label={t('borrow.approveExtend', 'Approve Extension')}>
                      <ActionIcon color="teal" variant="light" onClick={() => handleApproveExtend(b)}><IconCheck size={16} /></ActionIcon>
                    </Tooltip>
                    <Tooltip label={t('borrow.rejectExtend', 'Reject Extension')}>
                      <ActionIcon color="red" variant="subtle" onClick={() => handleRejectExtend(b)}><IconX size={16} /></ActionIcon>
                    </Tooltip>
                  </>
                )}
                <Tooltip label={t("common.print", "Print")}>
                  <ActionIcon color="gray" variant="subtle" onClick={async () => {
                    try {
                      const res = await downloadBorrowPdf(b.id, i18n.language === 'th' ? 'th' : 'en')
                      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
                      window.open(url, '_blank')
                      setTimeout(() => URL.revokeObjectURL(url), 30_000)
                    } catch { notifications.show({ color: 'red', message: t('borrow.pdfGenerationFailed', 'Failed to generate PDF') }) }
                  }}><IconPrinter size={16} /></ActionIcon>
                </Tooltip>
                <Tooltip label={t("borrow.viewHistory", "History")}>
                  <ActionIcon color="indigo" variant="subtle" onClick={() => handleOpenHistory(b)}><IconHistory size={16} /></ActionIcon>
                </Tooltip>
              </Group>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box p={isSmall ? 16 : 32}>
      <Group justify="space-between" mb="xl">
        <Box>
          <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: 24, fontWeight: 600, letterSpacing: '-0.3px' }}>{t('borrow.title','Borrow')}</Text>
          <Text size="sm" c="dimmed">{t('borrow.subtitle','Asset borrow requests')}</Text>
        </Box>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => openRequestModal()}
          style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}
        >
          {t('borrow.requestBorrow','Request Borrow')}
        </Button>
      </Group>

      {/* ── Summary Cards ── */}
      {!loading && (() => {
        const allData = isApprover ? allBorrows : myBorrows
        // นับ "เกินกำหนด" จากสถานะจริง (OVERDUE ที่ cron ตั้งให้) ก่อน แล้วเสริมด้วยรายการที่ยัง
        // ไม่ทันถูก cron แตะ (สถานะยังเป็น APPROVED/PARTIALLY_RETURNED แต่ dueDate ผ่านไปแล้ว) —
        // เดิมเช็คแค่แบบหลังอย่างเดียว พอ cron เปลี่ยนสถานะเป็น OVERDUE จริงๆ ตัวเลขนี้จะไม่นับอีกเลย
        const overdue = allData.filter(b =>
          b.status === 'OVERDUE' ||
          ((b.status === 'APPROVED' || b.status === 'PARTIALLY_RETURNED') && isPastDueDate(b.dueDate))
        )
        const cards = [
          { key: 'total',    label: t('common.total','Total'),                count: allData.length,                                                     color: '#64748b', tab: isApprover ? 'all' : 'my',  show: true },
          { key: 'mine',     label: t('common.myRequests','My Requests'),      count: myBorrows.length,                                                   color: '#8b5cf6', tab: 'my',       show: true },
          // ── Pending Approval ──────────────────────────────────────────────────
          // สำหรับ approver: หมายถึง "รอ 'ฉัน' อนุมัติ" — ดึงจาก GET /borrows/pending ซึ่ง backend
          // จำกัดสิทธิ์ไว้แค่ ADMIN/ASSET_MANAGER เท่านั้น (RolesGuard) ไปที่ tab='pending' ตามเดิม
          // สำหรับ user ทั่วไป: endpoint ข้างบนเรียกไม่ได้เลย (โดนบล็อก 403) เดิมเลยซ่อนการ์ดนี้ทิ้งไปหมด
          // ทั้งที่ user ทั่วไปก็มีคำขอของตัวเองที่ "ยังรออนุมัติ" อยู่เหมือนกัน แค่ความหมายต่างกัน
          // (ของ user = "คำขอของฉันที่ยังไม่ถูกตัดสินใจ" ไม่ใช่ "รายการที่ฉันต้องไปอนุมัติ") ดึงจาก
          // myBorrows ที่มีอยู่แล้ว (ไม่ต้องขอสิทธิ์เพิ่ม) กรอง status PENDING เอง แล้วไปที่ tab='my'
          // แทน tab='pending' ที่เข้าไม่ได้
          { key: 'pending',  label: t('borrow.pendingApproval','Pending Approval'), count: isApprover ? pending.length : myBorrows.filter(b => b.status === 'PENDING').length, color: '#eab308', tab: isApprover ? 'pending' : 'my',  show: true },
          { key: 'active',   label: t('borrow.activeBorrowed','Active / Borrowed'), count: allData.filter(b => b.status === 'APPROVED' || b.status === 'PARTIALLY_RETURNED' || b.status === 'OVERDUE').length, color: '#14b8a6', tab: isApprover ? 'all' : 'my', show: true },
          { key: 'overdue',  label: t('borrow.overdue','Overdue'),           count: overdue.length,                                                    color: '#ef4444', tab: isApprover ? 'all' : 'my',  show: true },
          { key: 'returned', label: t('borrow.returned','Returned'),          count: allData.filter(b => b.status === 'RETURNED').length,               color: '#6366f1', tab: isApprover ? 'all' : 'my',  show: true },
          { key: 'rejected', label: t('borrow.rejected','Rejected'),          count: allData.filter(b => b.status === 'REJECTED').length,               color: '#ef4444', tab: isApprover ? 'all' : 'my',  show: true },
        ].filter(c => c.show)

        return (
          <Group gap="xs" mb="lg" wrap="wrap" justify="flex-start">
            {cards.map(c => (
              <Paper key={c.key} withBorder radius="lg" p="sm"
                onClick={() => {
                  setActiveTab(c.tab)
                  setSelectedCard(c.key)
                  if (c.key === 'overdue') setStatusFilter('OVERDUE_ALL')
                  else if (c.key === 'returned') setStatusFilter('RETURNED')
                  else if (c.key === 'active') setStatusFilter('ACTIVE_ALL')
                  else if (c.key === 'rejected') setStatusFilter('REJECTED')
                  else if (c.key === 'pending' && !isApprover) setStatusFilter('PENDING')
                  else setStatusFilter(null)
                }}
                style={{
                  borderLeft: `4px solid ${c.color}`, cursor: 'pointer',
                  background: selectedCard === c.key ? '#f8fafc' : '#fff',
                  transition: 'all 0.12s',
                  width: isSmall ? 'calc(50% - 4px)' : 150, flexShrink: 0,
                }}>
                <Text size="xs" c="dimmed" fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.2 }}>{c.label}</Text>
                <Text size="lg" fw={800} style={{ color: c.color, fontFamily: 'Inter, sans-serif' }}>{c.count}</Text>
              </Paper>
            ))}
          </Group>
        )
      })()}

      {loading ? (
        <Center p="xl"><Loader color="teal" /></Center>
      ) : (
        <Box style={{ background: '#fff', borderRadius: 16, border: '1px solid #ebebeb', overflow: 'auto' }}>
          {/* Search + filter bar */}
          <Box style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>
            <Group gap="sm">
              <TextInput
                placeholder={t('borrow.searchPlaceholder','Search borrower, asset, reason...')}
                leftSection={<IconSearch size={14} />}
                value={search}
                onChange={e => setSearch(e.target.value)}
                size="xs"
                style={{ flex: 1, maxWidth: isMobile ? undefined : 300, width: isMobile ? '100%' : undefined }}
                styles={{ input: { borderColor: '#e2e8f0' } }}
              />
              {isMobile ? (
                <Indicator label={statusFilter ? 1 : undefined} disabled={!statusFilter} size={16} color="teal">
                  <Button variant="default" size="xs" leftSection={<IconFilter size={14} />} onClick={openFilterSheet}>
                    {t('assets.filterBtn', 'Filter')}
                  </Button>
                </Indicator>
              ) : (
                <Select
                  placeholder={t('borrow.allStatuses','All statuses')}
                  clearable size="xs"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  data={['PENDING','APPROVED','REJECTED','RETURNED','PARTIALLY_RETURNED','OVERDUE']}
                  style={{ width: 200 }}
                />
              )}
            </Group>
          </Box>

          {/* ── มือถือ: แผ่นตัวกรองเลื่อนขึ้นจากล่าง ── */}
          <Drawer opened={filterSheetOpen} onClose={() => setFilterSheetOpen(false)}
            position="bottom" size="45%" radius="lg 0 0 0"
            title={<Text fw={700}>{t('assets.filterBtn', 'Filter')}</Text>}>
            <Stack gap="md" pb={70}>
              <Select
                label={t('common.status', 'Status')}
                placeholder={t('borrow.allStatuses','All statuses')}
                clearable
                value={draftStatus}
                onChange={setDraftStatus}
                data={['PENDING','APPROVED','REJECTED','RETURNED','PARTIALLY_RETURNED','OVERDUE']}
              />
            </Stack>
            <Box style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', borderTop: '1px solid #e2e8f0', padding: 16 }}>
              <Group grow>
                <Button variant="default" onClick={clearFilterSheet}>{t('assets.clearFilters', 'Clear All')}</Button>
                <Button onClick={applyFilterSheet} style={{ background: 'var(--brand-primary,#0f172a)', color: '#fff' }}>
                  {t('assets.applyFilters', 'Apply Filters')}
                </Button>
              </Group>
            </Box>
          </Drawer>
          <Tabs value={activeTab} onChange={v => setActiveTab(v ?? (isApprover ? 'all' : 'my'))}>
            {isApprover && (
              <Tabs.Panel value="pending">
                <BorrowTable data={pending.filter(b => {
                  const q = search.toLowerCase()
                  return (!q || `brw-${String(b.id).padStart(4, '0')}`.includes(q) || b.borrowerName?.toLowerCase().includes(q) || b.reason?.toLowerCase().includes(q) || (b.assets ?? []).some((a: any) => a.assetCode?.toLowerCase().includes(q))) &&
                    matchesStatusFilter(b, statusFilter)
                })} showApprove />
              </Tabs.Panel>
            )}
            <Tabs.Panel value="my">
              <BorrowTable data={myBorrows.filter(b => {
                const q = search.toLowerCase()
                return (!q || `brw-${String(b.id).padStart(4, '0')}`.includes(q) || b.borrowerName?.toLowerCase().includes(q) || b.reason?.toLowerCase().includes(q) || (b.assets ?? []).some((a: any) => a.assetCode?.toLowerCase().includes(q))) &&
                  matchesStatusFilter(b, statusFilter)
              })} showActions />
            </Tabs.Panel>
            {isApprover && (
              <Tabs.Panel value="all">
                <BorrowTable data={allBorrows.filter(b => {
                  const q = search.toLowerCase()
                  return (!q || `brw-${String(b.id).padStart(4, '0')}`.includes(q) || b.borrowerName?.toLowerCase().includes(q) || b.reason?.toLowerCase().includes(q) || (b.assets ?? []).some((a: any) => a.assetCode?.toLowerCase().includes(q))) &&
                    matchesStatusFilter(b, statusFilter)
                })} />
              </Tabs.Panel>
            )}
          </Tabs>
        </Box>
      )}

      {/* ── Request Modal ── */}
      <Modal
        key={modalKey}
        opened={opened}
        onClose={close}
        title={<Text fw={600} style={{ fontFamily: 'Inter, sans-serif', fontSize: 18 }}>{editingBorrowId ? `${t('borrow.editBorrow','Edit Borrow')} #${editingBorrowId}` : t('borrow.requestBorrow','Request Borrow')}</Text>}
        size="xl"
        radius="lg"
        scrollAreaComponent={undefined}
      >
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light">
            {t("borrow.requestWillBeSent","Request will be sent to Admin / Asset Manager for approval before pickup")}
          </Alert>

          {/* Recipient */}
          <Divider label={t("borrow.recipient", "Recipient")} labelPosition="left" />
          <Select
            label={t("borrow.selectFromDirectory","Select from directory")}
            placeholder={t("common.searchRecipient","Search recipient...")}
            description={t("borrow.autoFillHint","Auto-fills name, position and email below")}
            data={recipients.map((r: any) => ({
              value: String(r.id),
              label: r.name + (r.position ? ` — ${r.position}` : '') + (r.email ? ` · ${r.email}` : ''),
            }))}
            value={null}
            onChange={(v) => {
              if (!v) return
              const rec = recipients.find((r: any) => String(r.id) === v)
              if (rec) setForm(p => ({
                ...p,
                recipientName:     rec.name     ?? '',
                recipientPosition: rec.position ?? '',
                recipientEmail:    rec.email    ?? '',
                borrowerEmail:     rec.email    ?? p.borrowerEmail,
              }))
            }}
            searchable clearable
          />
          <SimpleGrid cols={isSmall ? 1 : 2}>
            <TextInput label={t("borrow.recipientName","Recipient Name")} value={form.recipientName}
              onChange={(e) => setForm(p => ({ ...p, recipientName: e.target.value }))} />
            <TextInput label={t("borrow.positionTitle","Position / Title")} value={form.recipientPosition}
              onChange={(e) => setForm(p => ({ ...p, recipientPosition: e.target.value }))} />
            <TextInput label={t("borrow.recipientEmail","Recipient Email")} placeholder="recipient@company.com"
              value={form.recipientEmail}
              onChange={(e) => setForm(p => ({ ...p, recipientEmail: e.target.value }))} />
            <TextInput label={t("borrow.notificationEmail","Notification Email (leave blank to fill manually)")}
              placeholder={t('borrow.notifyEmailPlaceholder', 'yourname@company.com — notified when approved')}
              value={(form as any).notificationEmail ?? ''}
              onChange={(e) => setForm(p => ({ ...p, notificationEmail: e.target.value } as any))} />
          </SimpleGrid>

          {/* Select Assets */}
          <Divider label={t("borrow.selectAssets","Select Assets")} labelPosition="left" />

          {/* Sticky selected panel */}
          {form.assetIds.length > 0 && (
            <Box style={{ background: '#f0fdf4', border: '1.5px solid #0d9488', borderRadius: 10, padding: '10px 12px' }}>
              <Group justify="space-between" mb={6}>
                <Text size="xs" fw={700} c="teal">{t('borrow.assetsSelectedCount', { count: form.assetIds.length })}</Text>
                <Text size="xs" c="dimmed" style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => setForm(p => ({ ...p, assetIds: [] }))}>{t('common.clearAll', 'Clear all')}</Text>
              </Group>
              <Group gap={6} wrap="wrap">
                {form.assetIds.map((id: number) => {
                  const a = assets.find((x: any) => x.id === id)
                  if (!a) return null
                  return (
                    <Badge key={id} size="sm" color="teal" variant="light"
                      rightSection={
                        <Box style={{ cursor: 'pointer', marginLeft: 4 }}
                          onClick={e => { e.stopPropagation(); toggleAsset(id) }}>✕</Box>
                      }>{a.assetCode}</Badge>
                  )
                })}
              </Group>
            </Box>
          )}

          {/* Search + filter */}
          <Group gap={8}>
            <TextInput
              placeholder={t("borrow.searchByNameSerial","Search by name or serial number...")}
              size="xs" leftSection={<IconSearch size={13} />}
              value={assetSearch} onChange={e => setAssetSearch(e.target.value)}
              style={{ flex: 1 }} radius="md"
            />
            <Select
              placeholder={t("borrow.allCategories","All categories")} size="xs" clearable
              data={categories.map((c: any) => ({ value: c.code, label: localizedName(c, i18n.language) }))}
              value={categoryFilter} onChange={setCategoryFilter} style={{ width: 170 }}
            />
          </Group>

          <Box style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #ebebeb', borderRadius: 10, padding: 8 }}>
            {availableAssets.length === 0 ? (
              <Center p="md">
                <Text size="sm" c="dimmed">
                  {assetSearch ? t('borrow.noAssetsMatching', { query: assetSearch }) : t('borrow.noAvailableAssets', 'No available assets')}
                </Text>
              </Center>
            ) : (
              <SimpleGrid cols={isSmall ? 1 : 2} spacing="xs">
                {availableAssets.map((a: any) => {
                  const selected = form.assetIds.includes(a.id)
                  return (
                    <Box key={a.id} onClick={() => toggleAsset(a.id)}
                      style={{
                        display: 'flex', gap: 10, alignItems: 'center', padding: '8px 10px',
                        borderRadius: 8, cursor: 'pointer',
                        border: selected ? '2px solid #0d9488' : '2px solid transparent',
                        background: selected ? '#f0fdf4' : '#fafafa',
                        transition: 'all .15s',
                      }}>
                      {a.imageUrl ? (
                        <Image src={a.imageUrl} w={36} h={36} radius="sm" fit="cover" style={{ flexShrink: 0 }} />
                      ) : (
                        <Box w={36} h={36} style={{ background: '#eee', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <IconPhoto size={16} color="#bbb" />
                        </Box>
                      )}
                      <Box style={{ minWidth: 0, flex: 1 }}>
                        <Text size="xs" fw={700} style={{ fontFamily: 'Inter,sans-serif' }} lineClamp={1}>{a.assetCode}</Text>
                        <Text size="xs" lineClamp={1}>{a.name}</Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>{a.serialNumber}</Text>
                      </Box>
                      {selected && <IconCheck size={15} color="#0d9488" style={{ flexShrink: 0 }} />}
                    </Box>
                  )
                })}
              </SimpleGrid>
            )}
          </Box>

          {/* Details */}
          <Divider label={t('borrow.details', 'Details')} labelPosition="left" />
          <SimpleGrid cols={isSmall ? 1 : 2}>
            <Textarea label={t("borrow.reasonPlaceholder","Reason for borrowing")} minRows={2}
              value={form.reason}
              onChange={(e) => setForm(p => ({ ...p, reason: e.target.value }))} />
            <SmartDateInput
              label={t('borrow.expectedReturnDate', 'Expected return date')}
              placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
              value={form.dueDate}
              onChange={(v) => setForm(p => ({ ...p, dueDate: v }))}
              clearable
            />
          </SimpleGrid>

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={close}>{t("common.cancel","Cancel")}</Button>
            <Button
              loading={saving}
              onClick={handleRequest}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #ffffff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}
            >
              {t('borrow.submitRequest','Submit Request')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Approve Borrow Modal ── */}
      <Modal opened={approveModalOpen} onClose={closeApproveModal} size="sm" radius="lg"
        title={<Text fw={600} style={{ fontFamily: 'Inter,sans-serif', fontSize: 15 }}>{t('borrow.approveBorrow','Approve Borrow Request')}</Text>}>
        <Stack gap="md">
          {approvingBorrow && (
            <Box style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px' }}>
              <Text size="sm" fw={600}>{approvingBorrow.borrowerName}</Text>
              <Text size="xs" c="dimmed">{(approvingBorrow.assets ?? []).length} asset(s) · {(approvingBorrow.assets ?? []).map((a: any) => a.assetCode).join(', ')}</Text>
            </Box>
          )}
          <SmartDateInput
            label={t('borrow.dueDateLabel')}
            placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
            value={approveDue}
            onChange={setApproveDue}
            clearable
            description={t('borrow.dueDateHint')}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeApproveModal}>{t("common.cancel","Cancel")}</Button>
            <Button onClick={confirmApprove}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
              {t('common.approve', 'Approve')}
            </Button>
          </Group>
        </Stack>
      </Modal>
      <HistoryTimelineModal
        opened={historyOpened}
        onClose={closeHistory}
        title={`${t('borrow.history', 'Borrow History')} ${historyTarget ? `BRW-${String(historyTarget.id).padStart(4, '0')}` : ''}`}
        data={historyData}
        loading={loadingHistory}
        typeConfig={{
          BORROW:          { color: 'blue',   label: t('borrow.hist.requested', 'Request Created'), icon: '📝' },
          BORROW_APPROVED: { color: 'teal',   label: t('borrow.hist.approved', 'Approved'),          icon: '✅' },
          BORROW_REJECTED: { color: 'red',    label: t('borrow.hist.rejected', 'Rejected'),           icon: '❌' },
          RETURN:          { color: 'lime',   label: t('borrow.hist.returned', 'Returned'),           icon: '↩️' },
          BORROW_EXTEND_REQUESTED: { color: 'blue',  label: t('borrow.hist.extendRequested', 'Extend Requested'), icon: '📅' },
          BORROW_EXTEND_APPROVED:  { color: 'teal',  label: t('borrow.hist.extendApproved', 'Extend Approved'),   icon: '✅' },
          BORROW_EXTEND_REJECTED:  { color: 'red',   label: t('borrow.hist.extendRejected', 'Extend Rejected'),   icon: '❌' },
          BORROW_EXTEND_CANCELLED: { color: 'gray',  label: t('borrow.hist.extendCancelled', 'Extend Auto-Cancelled (Returned)'), icon: '🚫' },
        }}
      />
      <AssetQuickView asset={qvAsset} opened={qvOpen} onClose={() => setQvOpen(false)} />
    </Box>
  )
}
